// Pure route+guest → tab decision, pulled out of AvisosPage.tsx so the guest
// constraint is enforced in exactly one place instead of once per render
// branch.
//
// guestMode is a real, persisted session (authStore's `fd:guestMode`) — not
// merely "no token" — and App.tsx never redirects a guest away from a route.
// A guest can therefore be sitting on 'avisos.feedback' or plain 'avisos' by
// editing the hash, or by having that exact route restored from
// localStorage at startup (before this module ever asked what tab to show).
// If each render branch re-derives "is this guest allowed here" from
// `guestMode` individually, one missed check reopens the hole — which is
// exactly what happened: the Notices branch guarded itself, the Feedback
// placeholder did not. Routing every decision through this function means
// there is one guard to get right, and every current and future tab
// (Notices, Feedback, Help, and whatever else joins them) inherits it for
// free instead of needing its own `!guestMode`.
import type { PlayerRoute } from '../../app/appStore';

export type AvisosView = 'avisos' | 'feedback' | 'help';

/**
 * The one place that decides "is this guest allowed to see the view they're
 * headed to." Shared by avisosViewForRoute (route → view) and by
 * AvisosPage's avisosTargetView effect (an already-resolved view handed in
 * directly by an external caller, e.g. MePage) — both had this same
 * guestMode-clamps-to-help check, and duplicating it once already caused the
 * exact gap this file's header comment describes.
 */
export function clampAvisosViewForGuest(view: AvisosView, guestMode: boolean): AvisosView {
  return guestMode ? 'help' : view;
}

export function avisosViewForRoute(route: PlayerRoute, guestMode: boolean): AvisosView {
  if (route === 'avisos.help' || route === 'avisos.faq') return clampAvisosViewForGuest('help', guestMode);
  if (route === 'avisos.feedback') return clampAvisosViewForGuest('feedback', guestMode);
  return clampAvisosViewForGuest('avisos', guestMode);
}

/**
 * A day's worth of notices, in one block.
 *
 * `label` is a discriminator, not text: 'today' and 'yesterday' get translated
 * strings, and anything older shows `dayKey`'s date. Formatting lives in the
 * component so this stays a pure function with no locale to carry.
 */
export type NoticeGroup<T> = {
  /** Stable local-calendar-day key, e.g. "2026-08-22". Also the React key. */
  dayKey: string;
  label: 'today' | 'yesterday' | 'older';
  items: T[];
};

function dayKeyOf(seconds: number): string {
  const d = new Date(seconds * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Groups notices by local calendar day, newest day first.
 *
 * <p>The list used to be a flat run of rows that each repeated their own full
 * date ("22/08, 12:05 AM"), so telling this morning's notices from last week's
 * meant reading every row. A day heading says it once, which also lets the rows
 * themselves drop to a bare clock.
 *
 * <p><b>Order is preserved, never re-sorted.</b> The server decides what "newest"
 * means (it can pin or backdate a notice); re-sorting here would silently
 * disagree with the unread badge, which counts what the server sent.
 *
 * @param nowSeconds the instant "today" is measured against — passed in rather
 *   than read from the clock so this stays pure and testable across midnight
 */
export function groupNoticesByDay<T extends { ts: number }>(
  items: T[],
  nowSeconds: number,
): NoticeGroup<T>[] {
  const today = dayKeyOf(nowSeconds);
  const yesterday = dayKeyOf(nowSeconds - 86400);
  const groups: NoticeGroup<T>[] = [];
  for (const item of items) {
    // A missing or zero timestamp is not 1970 — it is "unknown", and it must not
    // open a 1970 heading at the bottom of the list. Such rows join whatever
    // group is open, or start today's if none is.
    // Index access, not `.at(-1)`: the project's TS lib target predates ES2022,
    // so `.at` type-checks nowhere even though node runs it fine — the unit
    // tests passed and only `tsc -b` caught it.
    const last: NoticeGroup<T> | undefined = groups[groups.length - 1];
    const key = Number.isFinite(item.ts) && item.ts > 0 ? dayKeyOf(item.ts) : (last?.dayKey ?? today);
    if (last && last.dayKey === key) {
      last.items.push(item);
      continue;
    }
    groups.push({
      dayKey: key,
      label: key === today ? 'today' : key === yesterday ? 'yesterday' : 'older',
      items: [item],
    });
  }
  return groups;
}
